import React from 'react';
import { Target, Eye, Award, Handshake, Zap, TrendingUp, CheckCircle } from 'lucide-react';

const About = () => {
  const values = [
    { icon: Award, title: 'Qualidade' },
    { icon: Eye, title: 'Transparência' },
    { icon: Zap, title: 'Agilidade' },
    { icon: Handshake, title: 'Parcerias' },
    { icon: TrendingUp, title: 'Resultados' }
  ];

  return (
    <section id="about" className="py-32 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#2D5F7F]/5 rounded-full blur-3xl -z-0"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#C9A961]/5 rounded-full blur-3xl -z-0"></div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-block mb-6">
            <span className="bg-[#C9A961]/10 text-[#b89850] text-sm font-semibold px-4 py-2 rounded-full">
              Sobre Nós
            </span>
          </div>
          <h2 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6">
            Quem Somos
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Sua ponte para o mercado global
          </p>
        </div>

        {/* Main Description with Image */}
        <div className="grid lg:grid-cols-2 gap-16 mb-24 items-center max-w-7xl mx-auto">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
              Conectando o mercado brasileiro aos <span className="text-[#C9A961]">melhores produtos internacionais</span>
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              A Inter Products é uma importadora especializada em soluções completas de importação. Atuamos com excelência na seleção, negociação e logística, garantindo qualidade, procedência e competitividade.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Com uma rede sólida de fornecedores globais e abordagem estratégica, oferecemos soluções que atendem diferentes segmentos, sempre com foco em eficiência, segurança e agilidade.
            </p>
            
            {/* Features List */}
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-6 h-6 rounded-full bg-[#C9A961]/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="w-4 h-4 text-[#C9A961]" />
                </div>
                <div>
                  <p className="text-gray-800 font-medium">Fornecedores homologados globalmente</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-6 h-6 rounded-full bg-[#C9A961]/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="w-4 h-4 text-[#C9A961]" />
                </div>
                <div>
                  <p className="text-gray-800 font-medium">Processos otimizados e transparentes</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-6 h-6 rounded-full bg-[#C9A961]/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="w-4 h-4 text-[#C9A961]" />
                </div>
                <div>
                  <p className="text-gray-800 font-medium">Suporte consultivo especializado</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1613690399151-65ea69478674"
                alt="Global Trade"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2D5F7F]/50 to-transparent"></div>
            </div>
            <div className="absolute -bottom-8 -right-8 bg-white rounded-2xl shadow-2xl p-8 max-w-xs">
              <p className="text-5xl font-bold text-[#2D5F7F] mb-2">15+</p>
              <p className="text-gray-700 font-medium">Anos de experiência global</p>
            </div>
          </div>
        </div>

        {/* Mission and Vision */}
        <div className="grid md:grid-cols-2 gap-8 mb-24 max-w-6xl mx-auto">
          <div className="relative bg-gradient-to-br from-[#2D5F7F] to-[#1e4159] rounded-3xl p-10 text-white overflow-hidden group hover:scale-105 transition-transform">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16"></div>
            <Target className="w-16 h-16 text-[#C9A961] mb-6 relative z-10" />
            <h3 className="text-3xl font-bold mb-4 relative z-10">Nossa Missão</h3>
            <p className="text-gray-100 leading-relaxed text-lg relative z-10">
              Facilitar o acesso a produtos importados de alta qualidade, simplificando processos e entregando soluções confiáveis que impulsionam o crescimento dos nossos clientes.
            </p>
          </div>

          <div className="relative bg-gradient-to-br from-[#C9A961] to-[#b89850] rounded-3xl p-10 text-white overflow-hidden group hover:scale-105 transition-transform">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <Eye className="w-16 h-16 text-white mb-6 relative z-10" />
            <h3 className="text-3xl font-bold mb-4 relative z-10">Nossa Visão</h3>
            <p className="text-gray-50 leading-relaxed text-lg relative z-10">
              Ser referência no mercado de importação, reconhecida pela excelência operacional, transparência e compromisso com resultados.
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="max-w-6xl mx-auto">
          <h3 className="text-4xl font-bold text-center text-gray-900 mb-16">
            Nossos Valores
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-100 rounded-2xl p-6 text-center hover:shadow-xl hover:border-[#C9A961]/30 hover:-translate-y-2 transition-all group"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#2D5F7F] to-[#1e4159] rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="font-bold text-gray-900 text-lg group-hover:text-[#2D5F7F] transition-colors">
                    {value.title}
                  </h4>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
